import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthContext } from './providers/AuthProvider'
import { LayoutMain } from './layouts/LayoutMain'
import { LayoutAuth } from './layouts/LayoutAuth'
import { RequireStoreContext } from '@features/auth/components/RequireStoreContext'
import { usePermissions } from '@shared/hooks/usePermissions'
import { useWarrantyPermissions } from '@features/warranties/hooks/useWarrantyPermissions'
import { useAuth, useCajeroStore } from '@features/auth/hooks/useAuth'
import { getStoreConfig, type StoreConfig } from '@features/organization/utils/storeConfig'
import { canAccessRepairs, type RuleResult } from '@features/organization/utils/storeRules'
import { isCorporateScope } from '@features/organization/utils/accessControl'
import type { Permission } from '@shared/types/roles.types'

const LoginPage = lazy(() =>
  import('@features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage }))
)
const PosPage = lazy(() =>
  import('@features/pos/pages/PosPage').then(m => ({ default: m.PosPage }))
)
const PendingSalesPanel = lazy(() =>
  import('@features/pos/components/PendingSalesPanel').then(m => ({ default: m.PendingSalesPanel }))
)
const DashboardPage = lazy(() =>
  import('@features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage }))
)
const InventoryPage = lazy(() =>
  import('@features/inventory/pages/InventoryPage').then(m => ({ default: m.InventoryPage }))
)
const ProductsPage = lazy(() =>
  import('@features/products/pages/ProductsPage').then(m => ({ default: m.ProductsPage }))
)
const CashMovementsPage = lazy(() =>
  import('@features/cash/pages/CashMovementsPage').then(m => ({ default: m.CashMovementsPage }))
)
const AkasiaImportPage = lazy(() =>
  import('@features/products/pages/AkasiaImportPage').then(m => ({ default: m.AkasiaImportPage }))
)
const ClientsPage = lazy(() =>
  import('@features/clients/pages/ClientsPage').then(m => ({ default: m.ClientsPage }))
)
const ShiftsPage = lazy(() =>
  import('@features/shifts/pages/ShiftsPage').then(m => ({ default: m.ShiftsPage }))
)
const ReturnsPage = lazy(() =>
  import('@features/returns/pages/ReturnsPage').then(m => ({ default: m.ReturnsPage }))
)
const SalesPage = lazy(() =>
  import('@features/sales/pages/SalesPage').then(m => ({ default: m.SalesPage }))
)
const HistoryPage = lazy(() =>
  import('@features/history/pages/HistoryPage').then(m => ({ default: m.HistoryPage }))
)
const ReportsPage = lazy(() =>
  import('@features/reports/pages/ReportsPage').then(m => ({ default: m.ReportsPage }))
)
const OrganizationPage = lazy(() =>
  import('@features/organization/pages/OrganizationPage').then(m => ({
    default: m.OrganizationPage,
  }))
)
const CorporatePage = lazy(() =>
  import('@features/organization/pages/CorporatePage').then(m => ({
    default: m.CorporatePage,
  }))
)
const SystemModulesPage = lazy(() =>
  import('@features/platform/pages/SystemModulesPage').then(m => ({
    default: m.SystemModulesPage,
  }))
)
const UsersPage = lazy(() =>
  import('@features/users/pages/UsersPage').then(m => ({ default: m.UsersPage }))
)
const SettingsPage = lazy(() =>
  import('@features/settings/pages/SettingsPage').then(m => ({ default: m.SettingsPage }))
)
const SuppliersPage = lazy(() =>
  import('@features/suppliers/pages/SuppliersPage').then(m => ({ default: m.SuppliersPage }))
)
const ProcurementPage = lazy(() =>
  import('@features/procurement/pages/ProcurementPage').then(m => ({ default: m.ProcurementPage }))
)
const PayablesPage = lazy(() =>
  import('@features/payables/pages/PayablesPage').then(m => ({ default: m.PayablesPage }))
)
const RepairsRoot = lazy(() =>
  import('@features/repairs/pages/RepairsRoot').then(m => ({ default: m.RepairsRoot }))
)
const PurchasesPage = lazy(() =>
  import('@features/purchases/pages/PurchasesPage').then(m => ({ default: m.PurchasesPage }))
)
const RepairPortalPage = lazy(() =>
  import('@features/portal-cliente/pages/RepairPortalPage').then(m => ({
    default: m.RepairPortalPage,
  }))
)
const WarrantiesPage = lazy(() =>
  import('@features/warranties/pages/WarrantiesPage').then(m => ({ default: m.WarrantiesPage }))
)
const TransfersPage = lazy(() =>
  import('@features/transfers/pages/TransfersPage').then(m => ({ default: m.TransfersPage }))
)
const MyWorkPage = lazy(() =>
  import('@features/transfers/pages/MyWorkPage').then(m => ({ default: m.MyWorkPage }))
)
const TransferVerificationsPage = lazy(
  () => import('@features/finance/pages/TransferVerificationsPage')
)
const AprobacionesPage = lazy(() =>
  import('@features/pos/pages/AprobacionesPage').then(m => ({ default: m.AprobacionesPage }))
)
const TasksPage = lazy(() =>
  import('@features/tasks/pages/TasksPage').then(m => ({ default: m.TasksPage }))
)

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center bg-surface-50">
      <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuthContext()
  if (loading) return <PageLoader />
  if (!session) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PendingActivationScreen({ onLogout }: { onLogout: () => void }) {
  return (
    <div className="flex h-dvh flex-col items-center justify-center bg-surface-50 px-6 text-center">
      <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-warning-100 text-4xl">
        🔒
      </div>
      <h1 className="mb-2 text-xl font-bold text-navy-900">Cuenta pendiente de activación</h1>
      <p className="mb-8 max-w-xs text-sm text-surface-600 leading-relaxed">
        Pide a un administrador que te vincule a una tienda y asigne tu rol en el sistema.
      </p>
      <button
        onClick={onLogout}
        className="rounded-lg bg-navy-800 px-8 py-3 text-sm font-semibold text-white hover:bg-navy-700 active:scale-95 transition-all"
      >
        Cerrar sesión
      </button>
    </div>
  )
}

/**
 * Verifica que el usuario tenga perfil válido en public.users.
 * Si profileError existe (o cajero es null tras cargar auth), bloquea el acceso.
 * Solo permite ver la pantalla de activación pendiente y cerrar sesión.
 */
function RequireProfile({ children }: { children: React.ReactNode }) {
  const { cajero, logout, isLoading: authLoading } = useAuth()
  const profileError = useCajeroStore(s => s.profileError)

  if (authLoading) return <PageLoader />
  if (profileError) return <PendingActivationScreen onLogout={() => void logout()} />
  if (!cajero) return <PageLoader />
  return <>{children}</>
}

/**
 * Protege una ruta por permiso.
 * Si el usuario no tiene el permiso requerido, redirige a `redirectTo` (por defecto /pos).
 * Acepta un permiso individual o un array donde CUALQUIERA concede acceso.
 * Muestra PageLoader mientras el perfil del cajero carga — evita flashes de redirección.
 */
function RequirePermission({
  permission,
  redirectTo = '/pos',
  storeGate,
  children,
}: {
  permission: Permission | Permission[]
  redirectTo?: string
  // Gate adicional por config de la tienda activa (p.ej. Taller apagado).
  // Opcional: sin él, el guard se comporta igual que antes.
  storeGate?: (cfg: StoreConfig) => RuleResult
  children: React.ReactNode
}) {
  const { can, isLoading } = usePermissions()
  const { cajero } = useCajeroStore()
  if (isLoading) return <PageLoader />
  const hasAccess = Array.isArray(permission) ? permission.some(p => can(p)) : can(permission)
  if (!hasAccess) return <Navigate to={redirectTo} replace />
  if (storeGate && !storeGate(getStoreConfig(cajero?.storeConfig ?? {})).allowed) {
    return <Navigate to={redirectTo} replace />
  }
  return <>{children}</>
}

/**
 * Guard de acceso a Garantías por permiso FINO (00327): warranties.view efectivo
 * (o owner). Espeja al servidor; ya no depende del legacy gestionar_garantias.
 */
function RequireWarrantyView({ children }: { children: React.ReactNode }) {
  const { canView, ready } = useWarrantyPermissions()
  if (!ready) return <PageLoader />
  if (!canView) return <Navigate to="/pos" replace />
  return <>{children}</>
}

/**
 * Guard de Modo Corporativo. Solo usuarios con scope global/business entran a
 * la Vista Corporativa (supervisión). Los usuarios operativos (scope store)
 * son redirigidos a su operación. No requiere activeStoreId.
 */
function RequireCorporate({ children }: { children: React.ReactNode }) {
  const { cajero } = useCajeroStore()
  if (!cajero) return <PageLoader />
  if (!isCorporateScope(cajero.accessScope)) return <Navigate to="/pos" replace />
  return <>{children}</>
}

export function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<LayoutAuth />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        <Route
          element={
            <RequireAuth>
              <RequireProfile>
                <RequireStoreContext>
                  <LayoutMain />
                </RequireStoreContext>
              </RequireProfile>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/pos" replace />} />

          {/* POS — bodega redirige a /inventory (su módulo natural) */}
          <Route
            path="/pos"
            element={
              <RequirePermission permission="vender" redirectTo="/inventory">
                <PosPage />
              </RequirePermission>
            }
          />

          {/* Reparaciones — requiere gestionar_reparaciones */}
          <Route
            path="/repairs"
            element={
              <RequirePermission permission="gestionar_reparaciones" storeGate={canAccessRepairs}>
                <RepairsRoot />
              </RequirePermission>
            }
          />

          {/* Clientes — cualquiera que tenga gestion_clientes */}
          <Route path="/clients" element={<ClientsPage />} />

          {/* P0-1 Fase 8B — Ventas pendientes offline (READ-ONLY, ruta oculta sin
              entrada de menú; no conectada al flujo de cobro). */}
          <Route
            path="/pos/pendientes"
            element={
              <div className="p-4">
                <PendingSalesPanel />
              </div>
            }
          />

          {/* Devoluciones — sin restricción de ruta (el módulo interno ya controla) */}
          <Route path="/returns" element={<ReturnsPage />} />

          {/* Garantías — requiere warranties.view (fino, 00327) */}
          <Route
            path="/garantias"
            element={
              <RequireWarrantyView>
                <WarrantiesPage />
              </RequireWarrantyView>
            }
          />

          {/* Mi trabajo — tareas guiadas (deriva de traspasos); requiere transferencias */}
          <Route
            path="/my-work"
            element={
              <RequirePermission permission="transferencias">
                <MyWorkPage />
              </RequirePermission>
            }
          />

          {/* Centro de tareas (motor transversal) — sin guard de ruta: el RLS acota a lo del
              usuario/su tienda/su empresa, así que cada quien ve sólo lo suyo. */}
          <Route path="/tasks" element={<TasksPage />} />
          {/* Aprobaciones de descuento (00408): sin guard de ruta; el servidor decide qué ve cada quien. */}
          <Route path="/aprobaciones" element={<AprobacionesPage />} />

          {/* Traspasos — requiere transferencias */}
          <Route
            path="/transfers"
            element={
              <RequirePermission permission="transferencias">
                <TransfersPage />
              </RequirePermission>
            }
          />

          {/* Verificación de transferencias (contador) — gate autoritativo por
              RBAC (payments.verify_transfer) dentro de la página + RLS en datos. */}
          <Route path="/transfer-verifications" element={<TransferVerificationsPage />} />

          {/* Inventario y productos — requieren ver_inventario */}
          <Route
            path="/inventory"
            element={
              <RequirePermission permission="ver_inventario">
                <InventoryPage />
              </RequirePermission>
            }
          />
          <Route
            path="/products"
            element={
              <RequirePermission permission="ver_inventario">
                <ProductsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/products/import-akasia"
            element={
              <RequirePermission permission="ver_inventario">
                <AkasiaImportPage />
              </RequirePermission>
            }
          />

          {/* Caja — requiere movimientos_caja */}
          {/* Caja — operar (movimientos_caja) o solo ver (ver_caja). La escritura
              real sigue gateada por RLS y por turno activo; abrir la ruta no da
              permisos nuevos de crear movimientos. */}
          <Route
            path="/cash-movements"
            element={
              <RequirePermission permission={['movimientos_caja', 'ver_caja']}>
                <CashMovementsPage />
              </RequirePermission>
            }
          />

          {/* Compras — requiere compras */}
          <Route
            path="/purchases"
            element={
              <RequirePermission permission="compras">
                <PurchasesPage />
              </RequirePermission>
            }
          />

          {/* Proveedores — modulo dedicado. Guard legacy compras; la RLS fina
              (suppliers.view/create/update) sigue mandando dentro. */}
          <Route
            path="/suppliers"
            element={
              <RequirePermission permission="compras">
                <SuppliersPage />
              </RequirePermission>
            }
          />

          {/* Abastecimiento — ordenes de compra, recepcion, proveedores y CxP en una
              sola superficie. Guardia de compras; el permiso fino manda dentro. */}
          <Route
            path="/procurement"
            element={
              <RequirePermission permission="compras">
                <ProcurementPage />
              </RequirePermission>
            }
          />

          {/* Cuentas por pagar — la deuda con proveedores nace de Compras, asi que
              comparte su guardia; el permiso fino (purchases.view) manda dentro. */}
          <Route
            path="/payables"
            element={
              <RequirePermission permission="compras">
                <PayablesPage />
              </RequirePermission>
            }
          />

          {/* Turnos — cajero ve los suyos (cerrar_turno), encargado/admin ven todos */}
          <Route
            path="/shifts"
            element={
              <RequirePermission permission={['cerrar_turno', 'ver_reportes_basicos']}>
                <ShiftsPage />
              </RequirePermission>
            }
          />

          {/* Ventas, Reportes — requieren ver_reportes_basicos */}
          <Route
            path="/sales"
            element={
              <RequirePermission permission="ver_reportes_basicos">
                <SalesPage />
              </RequirePermission>
            }
          />
          {/* Historial operativo unificado. `ver_historial_propio` sólo abre la
              pantalla; el ALCANCE real (todas las tiendas vs sólo las propias
              operaciones del cajero) lo resuelve el servidor. */}
          <Route
            path="/history"
            element={
              <RequirePermission permission="ver_historial_propio">
                <HistoryPage />
              </RequirePermission>
            }
          />
          {/* /reports NO se gatea por el permiso legacy: la autorización REAL la gobierna
              el resolver contextual del servidor (reports_access_context) dentro de la
              página, para que un reports.view fino habilite aunque el rol legacy no lo
              tenga, y un deny contextual muestre "No autorizado". Layout ya exige sesión. */}
          <Route path="/reports" element={<ReportsPage />} />
          {/* Inicio — abierto a cualquier usuario autenticado (protegido por
              RequireAuth/RequireProfile del layout). El contenido interno del
              dashboard no cambia. */}
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Vista Corporativa — supervisión sin operar. Scope global/business.
              RequireStoreContext deja pasar /corporate sin activeStoreId. */}
          <Route
            path="/corporate"
            element={
              <RequireCorporate>
                <CorporatePage />
              </RequireCorporate>
            }
          />

          {/* Corporativo → Módulos del sistema (Plataforma 1A). Supervisión y
              administración segura de capacidades. El servidor valida cada
              cambio; la ruta sólo restringe el acceso al modo corporativo. */}
          <Route
            path="/corporate/modules"
            element={
              <RequireCorporate>
                <SystemModulesPage />
              </RequireCorporate>
            }
          />

          {/* Usuarios — solo admin */}
          <Route
            path="/users"
            element={
              <RequirePermission permission="gestion_usuarios">
                <UsersPage />
              </RequirePermission>
            }
          />

          {/* Empresa y Tiendas — requiere configuracion */}
          <Route
            path="/organization"
            element={
              <RequirePermission permission="configuracion">
                <OrganizationPage />
              </RequirePermission>
            }
          />

          {/* Configuración del sistema — requiere configuracion */}
          <Route
            path="/settings"
            element={
              <RequirePermission permission="configuracion">
                <SettingsPage />
              </RequirePermission>
            }
          />
        </Route>

        {/* Portal público por token — sin RequireAuth, sin LayoutMain. El folio ya no es credencial. */}
        <Route path="/r/:token" element={<RepairPortalPage />} />

        <Route path="*" element={<Navigate to="/pos" replace />} />
      </Routes>
    </Suspense>
  )
}
